import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import ResponsiveClientLayout from "@/components/admin/ResponsiveClientLayout";

import ReservaCard from "@/components/ui/ReservaCard";
import reservaService, { Reserva } from "@/services/reservaService";
import { useAuth } from "@/contexts/AuthContext";
import { useSocket } from "@/hooks/useSocket";
import { AuthService } from "@/services/authService";
import { 
  Building, Clock, CheckCircle, AlertCircle, XCircle, Eye, RefreshCw, 
  AlertTriangle, Calendar, CreditCard, DollarSign, Filter, Search,
  MapPin, Users, Coins, TrendingUp, ArrowRight, Download, Loader2, History
} from "lucide-react";

// Usar interface do serviço + propriedades extras para compatibilidade
interface ReservationExtended extends Reserva {
  can_cancel?: boolean;
  refund_percentage?: number;
  payment_status?: "PENDENTE" | "PROCESSANDO" | "CONCLUIDO" | "REEMBOLSADO" | "REEMBOLSADO_PARCIAL" | "FALHA";
  stripe_payment_id?: string;
  valor_reembolsado?: number;
  tipo_pagamento?: "STRIPE" | "CREDITO";
}

const ClientReservations = () => {
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const [reservations, setReservations] = useState<ReservationExtended[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [selectedReservation, setSelectedReservation] = useState<ReservationExtended | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("TODAS");
  const [filterPaymentType, setFilterPaymentType] = useState<string>("TODOS");

  const [visibleActiveCount, setVisibleActiveCount] = useState<number>(10);
  const [visiblePastCount, setVisiblePastCount] = useState<number>(10);

  const socket = useSocket({
    autoConnect: !!user,
    userId: user?.id,
    token: AuthService.getToken() ?? undefined,
  });

  // ✅ Função para carregar reservas reais da API
  const loadReservations = React.useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      
      setLoading(false);
      return;
    }

    try {
      
      setLoading(true);
      setError(null);
      
      const response = await reservaService.getReservasByUser(user.id);
      
      
      if (response && response.data && Array.isArray(response.data)) {
        // Mapear dados da API para estrutura compatível
        const reservasFormatadas: ReservationExtended[] = response.data.map(reserva => {
          // ✅ NOVO: Lógica de cancelamento baseada no status E tempo
          const isStatusCancelable = reserva.status === "CONFIRMADO" || reserva.status === "RESERVADO";
          
          // ✅ CORRIGIDO: Função mais robusta para calcular tempo
          const calculateCanCancel = () => {
            try {
              const now = new Date();
              
              // Tratar diferentes formatos de data
              let dataFormatada = reserva.data;
              if (dataFormatada.includes('T')) {
                dataFormatada = dataFormatada.split('T')[0];
              }
              
              // Garantir formato YYYY-MM-DD
              if (dataFormatada.includes('/')) {
                const [dia, mes, ano] = dataFormatada.split('/');
                dataFormatada = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
              }
              
              // Criar data da reserva
              const [horaInicio] = reserva.bloco.hora_inicio.split(':');
              const reservationDateTime = new Date(`${dataFormatada}T${reserva.bloco.hora_inicio}:00`);
              
              // Calcular diferença em horas
              const diffMs = reservationDateTime.getTime() - now.getTime();
              const hoursUntilReservation = diffMs / (1000 * 60 * 60);
              
              const hasTimeToCancel = hoursUntilReservation > 0;
              
              return {
                hasTimeToCancel,
                hoursUntilReservation,
                reservationDateTime,
                dataFormatada
              };
            } catch (error) {
              
              return {
                hasTimeToCancel: false,
                hoursUntilReservation: 0,
                reservationDateTime: new Date(),
                dataFormatada: reserva.data
              };
            }
          };
          
          const timeInfo = calculateCanCancel();
          const canCancel = isStatusCancelable && timeInfo.hasTimeToCancel;
          
          return {
            ...reserva,
            // ✅ Lógica de cancelamento baseada no status E tempo
            can_cancel: canCancel,
            refund_percentage: canCancel ? 100 : 0, // Será recalculado na função calculateRefundInfo
            payment_status: "CONCLUIDO" as const,
            tipo_pagamento: reserva.pago_com_credito ? "CREDITO" as const : "STRIPE" as const
          };
        });
        
        setReservations(reservasFormatadas);
        
      } else {
        
        setReservations([]);
      }
    } catch (error) {
      
      setError('Erro ao carregar reservas. Tente novamente.');
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // ✅ Listener para atualizações de reservas via WebSocket
  useEffect(() => {
    if (socket.socket) {
      const handleReservasUpdate = (data: unknown) => {
        
        loadReservations();
      };

      socket.socket.on('reservas_atualizadas', handleReservasUpdate);

      return () => {
        if (socket.socket) {
          socket.socket.off('reservas_atualizadas', handleReservasUpdate);
        }
      };
    }
  }, [socket.socket, loadReservations]);

  // ✅ Carregar reservas ao montar componente e quando usuário mudar
  useEffect(() => {
    loadReservations();
  }, [isAuthenticated, user, loadReservations]);

  // ✅ Resetar contadores quando reservas mudarem
  useEffect(() => {
    if (reservations.length > 0) {
      setVisibleActiveCount(10);
      setVisiblePastCount(10);
    }
  }, [reservations.length]);

  // ✅ NOVO: Capturar mensagem de sucesso da navegação
  useEffect(() => {
    if (location.state && location.state.message && location.state.type === 'success') {
      setSuccessMessage(location.state.message);
      
      // Limpar o estado da navegação para evitar mostrar a mensagem novamente
      window.history.replaceState({}, document.title);
      
      // Remover mensagem após 5 segundos
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "CONFIRMADO": return <CheckCircle className="w-4 h-4" />;
      case "RESERVADO": return <Clock className="w-4 h-4" />;
      case "EM_USO": return <Clock className="w-4 h-4" />;
      case "USADO": return <CheckCircle className="w-4 h-4" />;
      case "CANCELADO": return <XCircle className="w-4 h-4" />;
      case "CONCLUIDO": return <CheckCircle className="w-4 h-4" />;
      case "EXPIRADO": return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "CONFIRMADO": return "bg-green-100 text-green-800 border-green-200";
      case "RESERVADO": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "EM_USO": return "bg-indigo-100 text-indigo-800 border-indigo-200";
      case "USADO": return "bg-blue-100 text-blue-800 border-blue-200";
      case "CANCELADO": return "bg-red-100 text-red-800 border-red-200";
      case "CONCLUIDO": return "bg-blue-100 text-blue-800 border-blue-200";
      case "EXPIRADO": return "bg-gray-100 text-gray-800 border-gray-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getPaymentStatusColor = (status?: string) => {
    switch (status) {
      case "CONCLUIDO": return "bg-green-100 text-green-800 border-green-200";
      case "PENDENTE": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "REEMBOLSADO": return "bg-purple-100 text-purple-800 border-purple-200";
      case "REEMBOLSADO_PARCIAL": return "bg-orange-100 text-orange-800 border-orange-200";
      case "FALHA": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handleViewDetails = (reservation: ReservationExtended) => {
    setSelectedReservation(reservation);
    setIsDetailDialogOpen(true);
  };

  const handleCancelReservation = (reservation: ReservationExtended) => {
    setSelectedReservation(reservation);
    setIsCancelDialogOpen(true);
  };

  const confirmCancelReservation = async () => {
    if (selectedReservation) {
      try {
        const motivoFinal = cancelReason.trim() || "Cancelamento solicitado pelo cliente";
        const refundInfo = calculateRefundInfo(selectedReservation);
        
        await reservaService.cancel(selectedReservation.id, motivoFinal);
        
        setIsCancelDialogOpen(false);
        setCancelReason("");
        setSelectedReservation(null);
        setError(null);
        setSuccessMessage(
          refundInfo.percentage > 0
            ? `Reserva cancelada. Reembolso previsto: R$ ${refundInfo.value.toFixed(2)}`
            : "Reserva cancelada com sucesso"
        );
      } catch (error) {
        setError("Erro ao cancelar reserva. Tente novamente.");
      }
    }
  };

  // Filtros inteligentes inspirados no Booking.com
  const filteredReservations = reservations.filter(reservation => {
    const matchesSearch = reservation.sala.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reservation.data.includes(searchTerm);
    const matchesStatus = filterStatus === "TODAS" || reservation.status === filterStatus;
    
    // ✅ CORRIGIDO: Usar campo correto da API
    const matchesPayment = filterPaymentType === "TODOS" || 
                          (filterPaymentType === "CREDITO" && reservation.pago_com_credito) ||
                          (filterPaymentType === "STRIPE" && !reservation.pago_com_credito);
    
    return matchesSearch && matchesStatus && matchesPayment;
  });

  const activeReservations = filteredReservations.filter(r => 
    r.status === "CONFIRMADO" || r.status === "RESERVADO" || r.status === "EM_USO"
  );
  
  const pastReservations = filteredReservations.filter(r => 
    r.status === "CONCLUIDO" || r.status === "CANCELADO" || r.status === "EXPIRADO" || r.status === "USADO"
  );

  // Estatísticas inspiradas no Airbnb
  const totalSpent = reservations
    .filter(r => r.payment_status === "CONCLUIDO")
    .reduce((sum, r) => sum + r.valor, 0);

  const totalRefunded = reservations
    .filter(r => r.payment_status === "REEMBOLSADO" || r.payment_status === "REEMBOLSADO_PARCIAL")
    .reduce((sum, r) => sum + (r.valor_reembolsado || 0), 0);

  const thisMonthSpent = reservations
    .filter(r => {
      const reservationDate = new Date(r.created_at);
      const now = new Date();
      return reservationDate.getMonth() === now.getMonth() && 
             reservationDate.getFullYear() === now.getFullYear() &&
             r.payment_status === "CONCLUIDO";
    })
    .reduce((sum, r) => sum + r.valor, 0);

  // ✅ NOVA: Função para calcular reembolso baseado no tempo
  const calculateRefundInfo = (reservation: ReservationExtended) => {
    if (!reservation.can_cancel) {
      return { percentage: 0, value: 0, description: "Cancelamento não permitido" };
    }

    const now = new Date();
    // ✅ CORRIGIDO: Tratar formato de data corretamente
    const dataReserva = reservation.data.includes('T') ? reservation.data.split('T')[0] : reservation.data;
    const reservationDateTime = new Date(`${dataReserva}T${reservation.bloco.hora_inicio}:00`);
    const hoursUntilReservation = (reservationDateTime.getTime() - now.getTime()) / (1000 * 60 * 60);

    let percentage = 0;
    let description = "";

    if (hoursUntilReservation > 48) {
      percentage = 100;
      description = "Reembolso integral (mais de 48h antes)";
    } else if (hoursUntilReservation > 24) {
      percentage = 50;
      description = "Reembolso de 50% (entre 24h e 48h antes)";
    } else {
      percentage = 0;
      description = "Sem reembolso (menos de 24h antes)";
    }

    const value = (reservation.valor * percentage) / 100;
    return { percentage, value, description };
  };

  const getRefundInfo = (reservation: ReservationExtended) => {
    const refundInfo = calculateRefundInfo(reservation);
    
    if (refundInfo.percentage === 0) {
      return "Sem reembolso";
    }
    
    return `${refundInfo.percentage}% - R$ ${refundInfo.value.toFixed(2)}`;
  };

  const renderReservationCard = (reservation: ReservationExtended) => (
    <Card key={reservation.id} className="hover:shadow-lg transition-all duration-200 border-l-4 border-l-blue-500">
      <CardContent className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <Building className="w-5 h-5 text-gray-600" />
              <h3 className="font-semibold text-lg text-gray-900">{reservation.sala.nome}</h3>
            </div>
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(reservation.data).toLocaleDateString('pt-BR')}
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {reservation.bloco.hora_inicio} - {reservation.bloco.hora_fim}
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">R$ {reservation.valor.toFixed(2)}</p>
            <p className="text-sm text-gray-500">
              {reservation.pago_com_credito ? "Pago com créditos" : "Pago com cartão"}
            </p>
          </div>
        </div>
        
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Badge className={`${getStatusColor(reservation.status)} border`}>
              {getStatusIcon(reservation.status)}
              <span className="ml-1">{reservation.status}</span>
            </Badge>
            {reservation.payment_status && (
              <Badge variant="outline" className={getPaymentStatusColor(reservation.payment_status)}>
                <CreditCard className="w-3 h-3 mr-1" />
                {reservation.payment_status === "CONCLUIDO" ? "Pago" : reservation.payment_status}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleViewDetails(reservation)}
            >
              <Eye className="w-4 h-4 mr-1" />
              Detalhes
            </Button>
            {reservation.can_cancel && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleCancelReservation(reservation)}
                className="text-red-600 hover:text-red-700"
              >
                Cancelar
              </Button>
            )}
          </div>
        </div>

        {/* Informações financeiras integradas */}
        {(reservation.valor_reembolsado || reservation.can_cancel) && (
          <div className="bg-gray-50 rounded-lg p-3 mt-3">
            <div className="flex items-center justify-between text-sm">
              {reservation.valor_reembolsado ? (
                <span className="text-purple-600">
                  <RefreshCw className="w-4 h-4 inline mr-1" />
                  Reembolsado: R$ {reservation.valor_reembolsado.toFixed(2)}
                </span>
              ) : reservation.can_cancel ? (
                <span className="text-green-600">
                  Reembolso disponível: {getRefundInfo(reservation)}
                </span>
              ) : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );

  // ✅ Estado de loading
  if (loading) {
    return (
      <ResponsiveClientLayout
        title="Minhas Reservas"
        subtitle="Carregando suas reservas..."
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Carregando suas reservas...</p>
          </div>
        </div>
      </ResponsiveClientLayout>
    );
  }

  // ✅ Estado de erro
  if (error) {
    return (
      <ResponsiveClientLayout
        title="Minhas Reservas"
        subtitle="Erro ao carregar reservas"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-4 text-red-600" />
            <p className="text-red-600 mb-4">{error}</p>
            <Button onClick={loadReservations} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Tentar Novamente
            </Button>
          </div>
        </div>
      </ResponsiveClientLayout>
    );
  }

  // ✅ Estado de usuário não autenticado
  if (!isAuthenticated) {
    return (
      <ResponsiveClientLayout
        title="Minhas Reservas"
        subtitle="Acesso negado"
      >
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <AlertCircle className="w-8 h-8 mx-auto mb-4 text-yellow-600" />
            <p className="text-gray-600">Você precisa estar logado para ver suas reservas</p>
          </div>
        </div>
      </ResponsiveClientLayout>
    );
  }

  return (
    <ResponsiveClientLayout
      title="Minhas Reservas"
      subtitle="Suas reservas ativas, histórico completo e informações financeiras"
    >
      {/* ✅ NOVO: Mensagem de sucesso */}
      {successMessage && (
        <div className="mb-6 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <span className="text-green-800 font-medium">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filters and Search - Inspirado no Booking.com */}
      {/* <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por sala ou data..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODAS">Todas</SelectItem>
                <SelectItem value="CONFIRMADO">Confirmadas</SelectItem>
                <SelectItem value="RESERVADO">Reservadas</SelectItem>
                <SelectItem value="CONCLUIDO">Concluídas</SelectItem>
                <SelectItem value="USADO">Usadas</SelectItem>
                <SelectItem value="CANCELADO">Canceladas</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterPaymentType} onValueChange={setFilterPaymentType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Pagamento" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="STRIPE">Cartão</SelectItem>
                <SelectItem value="CREDITO">Créditos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card> */}

      {/* Tabs Content - Inspirado no Airbnb */}
      <Tabs defaultValue="ativas" className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6">
          <TabsTrigger value="ativas" className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Próximas ({activeReservations.length})
          </TabsTrigger>
          <TabsTrigger value="historico" className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            Histórico ({pastReservations.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ativas" className="space-y-4">
          {activeReservations.length > 0 ? (
            <>
              {activeReservations.slice(0, visibleActiveCount).map(renderReservationCard)}
              {activeReservations.length > visibleActiveCount && (
                <div className="flex flex-col items-center gap-3 py-4">
                  <Button
                    variant="outline"
                    onClick={() => setVisibleActiveCount(prev => prev + 10)}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Carregar mais ({activeReservations.length - visibleActiveCount} restantes)
                  </Button>
                  <p className="text-sm text-gray-500">
                    Mostrando {visibleActiveCount} de {activeReservations.length} reservas ativas
                  </p>
                </div>
              )}
            </>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhuma reserva ativa</h3>
                <p className="text-gray-600 mb-4">Você não tem reservas confirmadas ou pendentes</p>
                <Link to="/client/calendar">
                  <Button>
                    <Calendar className="w-4 h-4 mr-2" />
                    Fazer Nova Reserva
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="historico" className="space-y-4">
          {pastReservations.length > 0 ? (
            <>
              {pastReservations.slice(0, visiblePastCount).map(renderReservationCard)}
              {pastReservations.length > visiblePastCount && (
                <div className="flex flex-col items-center gap-3 py-4">
                  <Button
                    variant="outline"
                    onClick={() => setVisiblePastCount(prev => prev + 10)}
                    className="flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Carregar mais ({pastReservations.length - visiblePastCount} restantes)
                  </Button>
                  <p className="text-sm text-gray-500">
                    Mostrando {visiblePastCount} de {pastReservations.length} reservas passadas
                  </p>
                </div>
              )}
            </>
          ) : (
            <Card className="text-center py-12">
              <CardContent>
                <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum histórico</h3>
                <p className="text-gray-600">Suas reservas passadas aparecerão aqui</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Detail Dialog - Melhorado */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              Detalhes da Reserva #{selectedReservation?.id}
            </DialogTitle>
            <DialogDescription>
              Informações completas da reserva e regras de cancelamento.
            </DialogDescription>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-6 py-4">
              {/* Informações Principais */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Sala</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedReservation.sala.nome}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Status</label>
                      <Badge className={`${getStatusColor(selectedReservation.status)} border mt-1`}>
                        {getStatusIcon(selectedReservation.status)}
                        <span className="ml-1">{selectedReservation.status}</span>
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Data</label>
                      <p className="text-sm font-medium">{new Date(selectedReservation.data).toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Horário</label>
                      <p className="text-sm font-medium">{selectedReservation.bloco.hora_inicio} - {selectedReservation.bloco.hora_fim}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Informações Financeiras */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Informações Financeiras
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">Valor Total</label>
                      <p className="text-2xl font-bold text-gray-900">R$ {selectedReservation.valor.toFixed(2)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">Forma de Pagamento</label>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className={getPaymentStatusColor(selectedReservation.payment_status)}>
                          {selectedReservation.pago_com_credito ? (
                            <><Coins className="w-3 h-3 mr-1" />Créditos</>
                          ) : (
                            <><CreditCard className="w-3 h-3 mr-1" />Cartão</>
                          )}
                        </Badge>
                        {selectedReservation.payment_status && (
                          <Badge variant="outline" className={getPaymentStatusColor(selectedReservation.payment_status)}>
                            {selectedReservation.payment_status === "CONCLUIDO" ? "Pago" : selectedReservation.payment_status}
                          </Badge>
                        )}
                      </div>
                      {selectedReservation.pago_com_credito && selectedReservation.valor_credito_usado && (
                        <p className="text-xs text-gray-500 mt-1">R$ {selectedReservation.valor_credito_usado.toFixed(2)} usado</p>
                      )}
                    </div>
                  </div>

                  {selectedReservation.valor_reembolsado && (
                    <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-purple-600" />
                        <span className="text-sm font-medium text-purple-800">
                          Valor Reembolsado: R$ {selectedReservation.valor_reembolsado.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}

                  {selectedReservation.stripe_payment_id && (
                    <div className="mt-4 text-xs text-gray-500">
                      ID do Pagamento: {selectedReservation.stripe_payment_id}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Informações de Sistema */}
              <Card>
                <CardContent className="p-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Criada em</label>
                      <p className="text-gray-700">{new Date(selectedReservation.created_at).toLocaleString('pt-BR')}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Última atualização</label>
                      <p className="text-gray-700">{new Date(selectedReservation.updated_at).toLocaleString('pt-BR')}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Política de Cancelamento */}
              {selectedReservation.can_cancel && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600" />
                      <span className="font-medium text-yellow-800">Política de Cancelamento</span>
                    </div>
                    
                    {/* Política resumida */}
                    <div className="mb-3">
                      <p className="text-xs text-yellow-600 mb-2">
                        <strong>Regras:</strong> +48h = 100% | 24-48h = 50% | -24h = 0%
                      </p>
                      <p className="text-sm text-yellow-700">
                        <strong>Esta reserva:</strong> {calculateRefundInfo(selectedReservation).description}
                      </p>
                      <p className="text-lg font-semibold text-yellow-800 mt-1">
                        Reembolso: R$ {calculateRefundInfo(selectedReservation).value.toFixed(2)}
                      </p>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsDetailDialogOpen(false);
                        handleCancelReservation(selectedReservation);
                      }}
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancelar Reserva
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog - Melhorado */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Cancelar Reserva
            </DialogTitle>
            <DialogDescription>
              Confirme os dados e finalize o cancelamento da reserva.
            </DialogDescription>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4 py-4">
              {/* Resumo da Reserva */}
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <p className="text-sm text-red-800 mb-2">
                    Você está prestes a cancelar sua reserva:
                  </p>
                  <div className="space-y-1">
                    <p className="font-semibold text-red-900">{selectedReservation.sala.nome}</p>
                    <p className="text-sm text-red-800">
                      {new Date(selectedReservation.data).toLocaleDateString('pt-BR', { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'long' 
                      })} às {selectedReservation.bloco.hora_inicio}
                    </p>
                    <p className="text-sm text-red-800">
                      Valor: <strong>R$ {selectedReservation.valor.toFixed(2)}</strong>
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Política de Cancelamento Completa */}
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-blue-600" />
                    <span className="font-medium text-blue-800">Política de Cancelamento</span>
                  </div>
                  
                  {/* Regras da Política */}
                  <div className="space-y-2 mb-4">
                    <div className="text-sm text-blue-700">
                      <strong>Regras de Reembolso:</strong>
                    </div>
                    <ul className="text-xs text-blue-600 space-y-1 ml-4">
                      <li>• <strong>Mais de 48h antes:</strong> Reembolso integral (100%)</li>
                      <li>• <strong>Entre 24h e 48h:</strong> Reembolso de 50% do valor</li>
                      <li>• <strong>Menos de 24h:</strong> Sem reembolso</li>
                    </ul>
                  </div>

                  {/* Cálculo para esta reserva */}
                  <div className="bg-white rounded p-3 border border-blue-200">
                    <p className="text-sm text-blue-700 mb-1">
                      <strong>Para esta reserva:</strong>
                    </p>
                    <p className="text-sm font-medium text-blue-800">
                      {calculateRefundInfo(selectedReservation).description}
                    </p>
                    <p className="text-lg font-bold text-blue-900 mt-1">
                      Valor do reembolso: R$ {calculateRefundInfo(selectedReservation).value.toFixed(2)}
                    </p>
                    {calculateRefundInfo(selectedReservation).percentage > 0 && (
                      <p className="text-xs text-blue-600 mt-2">
                        {selectedReservation.pago_com_credito 
                          ? 'O valor será creditado imediatamente em seu saldo.' 
                          : 'O valor será creditado em seu saldo para futuras reservas.'
                        }
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Campo de Motivo */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Motivo do cancelamento (opcional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Informe o motivo do cancelamento..."
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
              </div>

              {/* Botões de Ação */}
              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCancelDialogOpen(false);
                    setCancelReason("");
                  }}
                  className="flex-1"
                >
                  Manter Reserva
                </Button>
                <Button
                  onClick={confirmCancelReservation}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Confirmar Cancelamento
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ResponsiveClientLayout>
  );
};

export default ClientReservations; 
